/**
 * URL and Security Validation Utilities
 * Prevents common security vulnerabilities like open redirects
 */

/**
 * List of allowed redirect domains
 * Add your SaaS customer domains here
 */
const ALLOWED_REDIRECT_DOMAINS = [
  'simplecita.app',
  'nevozio.app',
  'revvly.app',
  'localhost:3000',
  'localhost:5173',
];

/**
 * Validate redirect URL against whitelist
 * Prevents open redirect attacks (CWE-601)
 *
 * @param url - URL to validate
 * @returns true if URL is safe to redirect to, false otherwise
 *
 * @example
 * if (isValidRedirectUrl('https://simplecita.app/dashboard')) {
 *   window.location.href = url;
 * }
 */
export function isValidRedirectUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Only allow HTTPS (or HTTP for localhost)
    const isHttps = urlObj.protocol === 'https:';
    const isLocalhost = urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1';
    const isHttp = urlObj.protocol === 'http:';

    if (!(isHttps || (isHttp && isLocalhost))) {
      return false;
    }

    // Check against whitelist
    const isAllowed = ALLOWED_REDIRECT_DOMAINS.some(domain => {
      const normalizedDomain = domain.toLowerCase();
      const normalizedHostname = urlObj.hostname.toLowerCase();

      // Exact match or subdomain match
      return (
        normalizedHostname === normalizedDomain ||
        normalizedHostname.endsWith('.' + normalizedDomain)
      );
    });

    return isAllowed;
  } catch {
    // Invalid URL format
    return false;
  }
}

/**
 * Get safe redirect URL or fallback
 * Uses provided URL if valid, otherwise returns fallback
 *
 * @param url - Attempted redirect URL
 * @param fallback - URL to use if validation fails (default: history.back())
 * @returns Valid redirect URL or null to use fallback
 */
export function getSafeRedirectUrl(url: string | null | undefined, fallback?: string): string | null {
  if (isValidRedirectUrl(url) && url) {
    return url;
  }
  return fallback || null;
}

/**
 * Perform safe redirect
 * Validates URL before redirecting
 *
 * @param url - URL to redirect to
 * @param fallback - Fallback action if URL is invalid
 */
export function safeRedirect(
  url: string | null | undefined,
  fallback: () => void = () => window.history.back()
): void {
  const safeUrl = getSafeRedirectUrl(url);

  if (safeUrl) {
    window.location.href = safeUrl;
  } else {
    fallback();
  }
}
